import { forwardRef, useRef } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import GeneralHelper from '../../Helpers/GeneralHelper';
import LoadingDots from '../LoadingAnimations/LoadingDots/LoadingDots';
import './NectarineButton.scss';

export type NectarineButtonColors = 'blue' | 'red' | 'white' | 'green' | 'gray' | 'black';

interface NectarineButtonProps {
  id?: string;
  text: string;
  type?: 'button' | 'submit';
  iconClassString?: string;
  onClick?: (() => void) | (() => Promise<void>);
  href?: string;
  hrefOpensNewTab?: boolean;
  // An optional click event that always fires, even if the button is disabled
  onClickValidationFn?: () => void;
  // An optional click event that only fires when the button is disabled
  onDisabledClickFn?: () => void;
  color?: NectarineButtonColors;
  size?: 'regular' | 'large' | 'medium' | 'smedium' | 'small' | 'wide';
  displayStyle?: 'block' | 'inline-block';
  isDisabled?: boolean;
  showLoadingDots?: boolean;
  tooltipText?: string | JSX.Element;
  disabledTooltipText?: string;
  allowClickEventToBubble?: boolean;
  title?: string;
}

const NectarineButton = forwardRef<HTMLButtonElement, NectarineButtonProps>((props: NectarineButtonProps, ref) => {
  // NOTE: We have to put a letter in front of the GUID, or else the UncontrolledTooltip breaks
  const id = useRef<string>(props.id ?? 'M' + GeneralHelper.generateGuid());

  const getColorClass = () => {
    switch (props.color) {
      case 'red':
        return 'btn-danger';

      case 'white':
        return 'btn-light';

      case 'green':
        return 'btn-success';

      case 'gray':
        return 'btn-secondary';

      case 'black':
        return 'btn-black';

      case 'blue':
      default:
        return 'btn-primary';
    }
  };

  const getButtonSizeCSSClass = () => {
    switch (props.size) {
      case 'regular':
        return '';

      case 'large':
        return 'isLargeButton';

      case 'small':
        return 'isSmallButton';

      case 'wide':
        return 'isWideButton';

      case 'smedium':
        return 'isSmediumButton';

      default:
        return 'isMediumButton';
    }
  };

  const getButtonJsx = (): JSX.Element => {
    return (
      <button
        ref={ref}
        type={props.type ?? 'button'}
        className={`btn ${getColorClass()} nectarineButton ${props.isDisabled ? 'isDisabled' : ''} ${!props.text.length ? 'noText' : ''}`}
        disabled={props.isDisabled ?? false}
        onClick={(e) => {
          if (!props.allowClickEventToBubble && !props.href && props.type !== 'submit') {
            e.preventDefault();
            e.stopPropagation();
          }

          props.onClickValidationFn?.();

          // If we have an href, we can stop here without calling the onClick event
          if (props.href) {
            return;
          }

          if (!props.isDisabled) {
            if (props.type === 'submit') {
              // For submit buttons, just return and let the event bubble up
              return;
            } else {
              props.onClick?.();
            }
          }
        }}
        onKeyDown={(e) => {
          if ((!props.isDisabled && e.key === ' ') || e.key === 'Enter') {
            props.onClick?.();
          }
        }}
      >
        <div className={`nectarineButtonContent`}>
          {props.iconClassString ? (
            <span className={props.text.length ? 'me-2' : ''}>
              <span className={props.iconClassString}></span>
            </span>
          ) : (
            <></>
          )}

          {props.text.length ? <span className={`buttonText ${props.text.length > 40 ? 'isLongText' : ''}`}>{props.text}</span> : <></>}

          <div className={`loadingDotsWrapper ${props.showLoadingDots ? 'isDisplayed' : ''}`}>
            <LoadingDots />
          </div>
        </div>
      </button>
    );
  };

  return (
    <div
      id={id.current}
      className={`buttonWrapper ${props.displayStyle === 'block' ? 'isBlock' : 'd-inline-block'} ${getButtonSizeCSSClass()}`}
      title={props.title || undefined}
    >
      {/* Include an overlay so we can still receive a click event and run a validation function if we need */}
      {props.isDisabled ? (
        <div
          className="disabledButtonOverlay"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            props.onClickValidationFn?.();

            props.onDisabledClickFn?.();
          }}
        ></div>
      ) : (
        <></>
      )}

      {props.isDisabled && props.disabledTooltipText ? (
        <UncontrolledTooltip target={id.current}>{props.disabledTooltipText}</UncontrolledTooltip>
      ) : (
        <></>
      )}

      {props.tooltipText ? <UncontrolledTooltip target={id.current}>{props.tooltipText}</UncontrolledTooltip> : <></>}

      {props.href ? (
        <a href={props.href} target={(props.hrefOpensNewTab ?? true) ? '_blank' : undefined} rel="noopener noreferrer">
          {getButtonJsx()}
        </a>
      ) : (
        <>{getButtonJsx()}</>
      )}
    </div>
  );
});

NectarineButton.displayName = 'NectarineButton';

export default NectarineButton;
